import { useEffect, useRef, useState } from 'react';
import { Icon } from '../components/Icon';
import { Loading, ErrorView } from '../components/ViewState';
import { api } from '../data/api';
import type { MessageTemplate } from '../data/types';

// Libreria condivisa di modelli messaggio (WhatsApp/Email). Scelta di prodotto:
// TUTTI globali, chiunque può creare/modificare/eliminare — nessuno scope per
// spazio. La compose dei lead (LeadDrawer) riempie i segnaposto da sola.
type Channel = 'whatsapp' | 'email';
const CHANNEL_LABEL: Record<Channel, string> = { whatsapp: 'WhatsApp', email: 'Email' };

// Segnaposto realmente sostituiti in LeadDrawer.tsx (tienili allineati).
const PLACEHOLDERS: { tag: string; desc: string }[] = [
  { tag: '{nome}', desc: 'nome del locale' },
  { tag: '{citta}', desc: 'città' },
  { tag: '{categoria}', desc: 'categoria (Ristorante, Pizzeria…)' },
];

const preview = (s: string): string => {
  const flat = s.replace(/\s+/g, ' ').trim();
  return flat.length > 130 ? flat.slice(0, 130) + '…' : flat;
};

export function TemplatesView() {
  const [items, setItems] = useState<MessageTemplate[] | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [editing, setEditing] = useState<MessageTemplate | 'new' | null>(null);

  const load = async () => {
    try {
      setItems(await api.leadTemplates());
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    }
  };
  useEffect(() => {
    void load();
  }, []);

  if (err) return <ErrorView detail={err} />;
  if (!items) return <Loading />;

  const channels: Channel[] = ['whatsapp', 'email'];

  return (
    <div className="view">
      <div className="view-pad rs-stack">
        <div className="rs-actionbar">
          <button type="button" className="btn btn-primary" onClick={() => setEditing('new')}>
            <Icon name="plus" size={16} />
            <span className="btn-label">Nuovo modello</span>
          </button>
        </div>

        <div className="rs-note">
          Questi modelli sono <b>condivisi da tutti</b>: tu, Luigi e Sabino vedete e usate gli stessi, e ognuno può
          crearli, modificarli ed eliminarli. Quando scrivi a un lead li trovi pronti nella finestra del messaggio.{' '}
          I segnaposto{' '}
          {PLACEHOLDERS.map((p, i) => (
            <span key={p.tag}>
              <code>{p.tag}</code>
              {i < PLACEHOLDERS.length - 1 ? ' ' : ''}
            </span>
          ))}{' '}
          si riempiono da soli col locale giusto.
        </div>

        {channels.map((ch) => {
          const list = items.filter((t) => t.channel === ch);
          return (
            <div className="rs-card" key={ch}>
              <div className="rs-card-head">
                <span className="rs-card-title">{CHANNEL_LABEL[ch]}</span>
                <span className="rs-muted" style={{ fontSize: 12 }}>
                  {list.length} {list.length === 1 ? 'modello' : 'modelli'}
                </span>
              </div>
              {list.length ? (
                <div>
                  {list.map((t) => (
                    <div
                      key={t.id}
                      className="tpl-row"
                      role="button"
                      tabIndex={0}
                      onClick={() => setEditing(t)}
                      onKeyDown={(ev) => {
                        if (ev.key === 'Enter') setEditing(t);
                      }}
                    >
                      <span className="tpl-main">
                        <span className="rs-cli-name">{t.name}</span>
                        <span className="tpl-prev">
                          {ch === 'email' && t.subject ? <b>{t.subject} · </b> : null}
                          {preview(t.body)}
                        </span>
                      </span>
                      <span className="tpl-chev">
                        <Icon name="edit" size={15} />
                      </span>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="rs-muted" style={{ fontSize: 13, padding: '6px 6px 2px' }}>
                  Ancora nessun modello {CHANNEL_LABEL[ch]}. Creane uno con <b>Nuovo modello</b>.
                </div>
              )}
            </div>
          );
        })}
      </div>

      {editing && (
        <TemplateModal
          template={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            void load();
          }}
        />
      )}
    </div>
  );
}

function TemplateModal({
  template,
  onClose,
  onSaved,
}: {
  template: MessageTemplate | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [channel, setChannel] = useState<Channel>((template?.channel as Channel) ?? 'whatsapp');
  const [name, setName] = useState(template?.name ?? '');
  const [subject, setSubject] = useState(template?.subject ?? '');
  const [body, setBody] = useState(template?.body ?? '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const bodyRef = useRef<HTMLTextAreaElement>(null);

  const valid = name.trim().length > 0 && body.trim().length > 0;

  /** Inserisce un segnaposto al cursore (o in coda se il campo non ha focus). */
  const insertTag = (tag: string) => {
    const el = bodyRef.current;
    if (!el) {
      setBody((b) => b + tag);
      return;
    }
    const start = el.selectionStart ?? body.length;
    const end = el.selectionEnd ?? body.length;
    setBody(body.slice(0, start) + tag + body.slice(end));
    requestAnimationFrame(() => {
      el.focus();
      const pos = start + tag.length;
      el.setSelectionRange(pos, pos);
    });
  };

  const submit = async () => {
    if (!valid || busy) return;
    setBusy(true);
    setError(null);
    const input = {
      channel,
      name: name.trim(),
      body: body.trim(),
      ...(channel === 'email' && subject.trim() ? { subject: subject.trim() } : {}),
    };
    try {
      if (template) await api.updateTemplate(template.id, input);
      else await api.createTemplate(input);
      onSaved();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      setBusy(false);
    }
  };

  const remove = async () => {
    if (!template || busy) return;
    // eslint-disable-next-line no-alert
    if (!confirm(`Eliminare il modello "${template.name}"?`)) return;
    setBusy(true);
    setError(null);
    try {
      await api.deleteTemplate(template.id);
      onSaved();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      setBusy(false);
    }
  };

  return (
    <div className="mscrim open" onMouseDown={onClose}>
      <div
        className="modal"
        role="dialog"
        aria-modal="true"
        aria-label={template ? 'Modifica modello' : 'Nuovo modello'}
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className="modal-head">
          <h3>{template ? 'Modifica modello' : 'Nuovo modello'}</h3>
          <button type="button" className="dr-close" onClick={onClose} aria-label="Chiudi">
            <Icon name="x" size={18} />
          </button>
        </div>
        <div className="modal-body">
          <div className="field-row">
            <div className="field">
              <label>Canale</label>
              <select className="input" value={channel} onChange={(e) => setChannel(e.target.value as Channel)}>
                <option value="whatsapp">WhatsApp</option>
                <option value="email">Email</option>
              </select>
            </div>
            <div className="field">
              <label>Nome del modello</label>
              <input
                className="input"
                value={name}
                autoFocus
                placeholder="Es. WhatsApp · Primo contatto"
                onChange={(e) => setName(e.target.value)}
              />
            </div>
          </div>
          {channel === 'email' && (
            <div className="field">
              <label>Oggetto</label>
              <input
                className="input"
                value={subject}
                placeholder="Oggetto dell'email"
                onChange={(e) => setSubject(e.target.value)}
              />
            </div>
          )}
          <div className="field" style={{ marginBottom: 8 }}>
            <label>Testo</label>
            <textarea
              ref={bodyRef}
              className="textarea tpl-body-area"
              value={body}
              placeholder="Scrivi il messaggio… clicca un segnaposto qui sotto per inserirlo."
              onChange={(e) => setBody(e.target.value)}
            />
          </div>
          <div className="tpl-tags">
            {PLACEHOLDERS.map((p) => (
              <button
                type="button"
                key={p.tag}
                className="tpl-tag"
                title={`Inserisci ${p.tag} — ${p.desc}`}
                onClick={() => insertTag(p.tag)}
              >
                {p.tag}
              </button>
            ))}
          </div>
          <p style={{ fontSize: 11.5, color: 'var(--mute)', margin: '8px 2px 0', lineHeight: 1.5 }}>
            Si riempiono da soli: <code>{'{nome}'}</code> = nome del locale, <code>{'{citta}'}</code> = città,{' '}
            <code>{'{categoria}'}</code> = tipo.
          </p>
          {error && <div className="rs-error" style={{ marginTop: 12 }}>{error}</div>}
        </div>
        <div className="modal-foot">
          {template && (
            <button type="button" className="btn btn-ghost text-danger" onClick={remove} disabled={busy}>
              Elimina
            </button>
          )}
          <div className="grow" />
          <button type="button" className="btn btn-ghost" onClick={onClose} disabled={busy}>
            Annulla
          </button>
          <button type="button" className="btn btn-primary" onClick={submit} disabled={!valid || busy}>
            {busy ? 'Salvo…' : 'Salva'}
          </button>
        </div>
      </div>
    </div>
  );
}
